import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { faArrowDown, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { useFormikContext } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { REPOSITORY } from '../../../../api/repository/Repository'
import MultiSheetCreator from '../../../../components/ExcelCreator/MultiSheetCreator'
import FileDownloader from '../../../../components/FileDownloader/FileDownloader'
import FileUploader from '../../../../components/FileUploader/FileUploader'
import IconButton from '../../../../components/IconButton/IconButton'
import Table from '../../../../components/Table/Table'
import UnderlineHeading from '../../../../components/UnderlineText/UnderlineHeading'
import Warning from '../../../../components/Warning/Warning'
import {
  cleanRut,
  isNoNominative,
  isValidRut,
} from '../../../../utils/formats/validators'
import { useLoadingContext } from '../../../../utils/hooks/Context/LoadingContext'
import { useRefillContext } from '../../../../utils/hooks/Context/RefillContext'
import { useLabels } from '../../../../utils/hooks/useLabels/useLabels'
import { colors } from '../../../../utils/theme/styles'
import useShowDetails from '../../hooks/useShowDetails'
import useNoNominativeFile from './hooks/useNoNominativeFile'
import { DataLoadError } from '../../../../components/DataLoadError/DataLoadError'

const fileKeysToObjectKeys = {
  Monto: 'nMonto',
  Rut: 'sEmpleado',
}

const fileObjectFormat = (object) =>
  Object.keys(object).reduce((acc, el) => {
    if (el === 'Rut') {
      // Los identificadores NN no son RUT: se dejan tal cual (sin limpiar) para
      // no romper el correlativo; solo los RUT se limpian a dígitos + DV.
      const raw = object[el]
      return {
        ...acc,
        [fileKeysToObjectKeys[el]]: isNoNominative(raw)
          ? `${raw ?? ''}`.trim()
          : cleanRut(raw),
      }
    }
    return { ...acc, [fileKeysToObjectKeys[el]]: object[el] }
  }, {})

const fileDataFormat = (data) => {
  if (data[0]?.Tarjeta) {
    return data.reduce((acc, item) => {
      if (item['Monto']) {
        return [
          ...acc,
          {
            sEmpleado: item['Nombre Tarjeta'],
            nMonto: item['Monto'],
            sComentario: '',
            centroCosto: item?.Sucursal || '',
          },
        ]
      }
      return [
        ...acc,
        {
          sEmpleado: item['Nombre Tarjeta'],
          nMonto: 0,
          sComentario: '',
          centroCosto: item?.Sucursal || '',
        },
      ]
    }, [])
  } else if (data[0].Rut) {
    return data.map((item) => ({
      ...fileObjectFormat(item),
      sTarjeta: '',
      sComentario: '',
      centroCosto: '',
    }))
  }
}

const dataLoadErrorColumns = [
  {
    Header: 'Rut',
    accessor: 'Rut',
  },
  {
    Header: 'Mensaje',
    accessor: 'sMensaje',
  },
]

function Massive() {
  const { texts } = useLabels()
  const [observations, setObservations] = useState([])
  const [data, setData] = useState([])
  const [isRutFile, setIsRutFile] = useState(false)

  const { cEmpleados, setCEmpleados, totalAmount } = useRefillContext()

  const [showDataLoadError, setShowDataLoadError] = useState(false)
  const [dataLoadError, setDataLoadError] = useState()

  const {
    values: { sSucursal, sCentroCosto },
    handleSubmit,
    isValid,
  } = useFormikContext()

  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()

  const { showDetails, boxRef, onShowAmount, onCLose } = useShowDetails()

  const { headers, noNominativeData } = useNoNominativeFile()

  const handleFile = useCallback((fileData) => {
    handleOpenLoading()
    setData([])
    setCEmpleados([])
    setObservations([])
    setShowDataLoadError(false)
    setTimeout(() => {
      if (fileData) {
        setIsRutFile(Boolean(fileData?.[0]?.Rut) && !fileData?.[0]?.Tarjeta)
        const formated = fileDataFormat(fileData)
        setData([...formated])
      }
      handleCLoseLoading()
    }, [800])
  }, [])

  const validate = useCallback(async () => {
    if (data.length > 0) {
      handleOpenLoading()

      // Validación local de RUT (solo para la carga nominativa por RUT, donde el
      // front exige "sin puntos y con guión"). Las filas con RUT inválido se
      // marcan como observación y NO se envían al backend. Los identificadores
      // NN (no nominativos) NO tienen dígito verificador: pasan sin validar.
      let dataToValidate = data
      const localObservations = []
      if (isRutFile) {
        const validRows = []
        data.forEach((element) => {
          if (
            isNoNominative(element.sEmpleado) ||
            isValidRut(element.sEmpleado)
          ) {
            validRows.push(element)
          } else {
            localObservations.push({
              Rut: element.sEmpleado,
              nMonto: element.nMonto,
              sMensaje:
                'RUT inválido: revise el dígito verificador (sin puntos y con guión)',
            })
          }
        })
        dataToValidate = validRows
      }

      if (dataToValidate.length > 0) {
        await REPOSITORY.wsEmpresa.refills
          .validateMassiveNominative({
            sucursal: sSucursal,
            centroCosto: sCentroCosto,
            datos: dataToValidate.map((element) => ({
              Rut: element.sEmpleado,
              nMonto: element.nMonto,
              centroCosto:
                element?.CentroCosto || element?.centroCosto || sCentroCosto,
            })),
          })
          .then((res) => {
            const backendObs = Array.isArray(res.data.observaciones)
              ? res.data.observaciones
              : []
            const allObservations = [...localObservations, ...backendObs]
            if (allObservations.length > 0) {
              setObservations(allObservations)
              setDataLoadError(allObservations)
              setCEmpleados(res.data.datosCorrectos)
              setShowDataLoadError(true)
            } else {
              setCEmpleados(res.data.datosCorrectos)
            }
          })
          .catch(() => {
            setCEmpleados([])
            setObservations(localObservations)
            setDataLoadError(localObservations)
            setShowDataLoadError(localObservations.length > 0)
          })
      } else {
        // Todas las filas tienen RUT inválido: no se llama al backend.
        setCEmpleados([])
        setObservations(localObservations)
        setDataLoadError(localObservations)
        setShowDataLoadError(localObservations.length > 0)
      }

      handleCLoseLoading()
      if (!showDetails) {
        onShowAmount()
      }
    }
  }, [showDetails, data, sSucursal, sCentroCosto, isRutFile])

  useEffect(() => {
    if (!data.length > 0) onCLose()
  }, [data])

  return (
    <>
      <DataLoadError
        data={dataLoadError}
        show={showDataLoadError}
        columns={dataLoadErrorColumns}
        onClose={() => {
          setShowDataLoadError(false)
        }}
      />
      <Flex direction="column" mb="48px">
        <Warning warningText={texts.massive_advertise} />
        <Text variant="subtitle3" mt="16px">
          * Recarga masiva Nominativa: Rut debe ir sin puntos y con guión
        </Text>
        <HStack spacing="82px" mt="45px" alignItems="flex-start">
          <VStack spacing="16px">
            <FileDownloader
              icon={faArrowDown}
              linkToFile="/files/Amipass_Recarga.xlsx"
              downloadText={'Carga masiva por RUT'}
            />
            <MultiSheetCreator
              filename={'RecargasTarjetasNN'}
              data={[noNominativeData]}
              sheets={[headers]}
              icon={faArrowDown}
              pageNames={['Datos']}
              label="Carga masiva por NN"
              variant="download"
            />
            {/* <FileDownloader
            linkToFile="/files/RecargasTarjetasNN_RecargasTarjetasNN.xlsxs"
            downloadText={'Carga masiva por NN'}
          /> */}
          </VStack>
          <Flex direction="column">
            <UnderlineHeading variant="h5" text={texts.upload_excel_template} />
            <Box mt="43px">
              <FileUploader handleFile={handleFile} />
            </Box>
          </Flex>
          <Box>
            <Button
              mt="90px"
              border={0}
              onClick={validate}
              bg={data.length > 0 ? '#0086D6' : '#BBBBBB'}
              boxShadow="0px 2px 26px rgba(225, 225, 225, 0.1)"
              cursor={data.length > 0 ? 'pointer' : 'not-allowed'}
            >
              Verificar información
            </Button>
          </Box>
        </HStack>
        {showDetails && (
          <VStack w="100%" alignItems="flex-start" mt="50px" spacing="16px">
            {observations.length > 0 && (
              <>
                <UnderlineHeading
                  text={'Datos con observaciones'}
                  variant="h5"
                />
                <Box mt="100px" w="100%" ref={boxRef}>
                  <Table
                    columns={[
                      { Header: 'Rut / NN', accessor: 'Rut', type: 'dni' },
                      { Header: 'Monto', accessor: 'nMonto' },
                      { Header: 'Observacion', accessor: 'sMensaje' },
                    ]}
                    data={observations}
                  />
                </Box>
              </>
            )}
            <UnderlineHeading mt="50px" text={'Datos Correctos'} variant="h5" />
            <Box w="100%" ref={boxRef}>
              <Table
                columns={[
                  { Header: 'Rut / NN', accessor: 'sEmpleado', type: 'dni' },
                  { Header: 'Colaborador', accessor: 'sNombre' },
                  { Header: 'Tarjeta', accessor: 'sTarjeta', type: 'card' },
                  { Header: 'Monto', accessor: 'nMonto', type: 'currency' },
                ]}
                data={cEmpleados}
              />
            </Box>
          </VStack>
        )}
        {cEmpleados.length > 0 && (
          <Box
            onClick={
              cEmpleados.length > 0 && totalAmount > 0
                ? handleSubmit
                : () => null
            }
            maxW="20vw"
            h="6vh"
            mt="48px"
          >
            <IconButton
              variant={
                cEmpleados.length > 0 && totalAmount > 0 && isValid
                  ? 'secondary'
                  : 'gray'
              }
              icon={faCheckCircle}
              color={
                cEmpleados.length > 0 && totalAmount > 0
                  ? 'FFFFFF'
                  : colors.gray[900]
              }
              text={texts.do_recharge}
            />
          </Box>
        )}
      </Flex>
    </>
  )
}
export default Massive
